import Seo from "../../components/seo";
import Layout from "../../components/layout";
import Header from "../../components/header";
import Footer from "../../components/footer";
import ServiceHero from "../../components/ServiceHero";
import ServiceCTA from "../../components/ServiceCTA";
import { CardColumn, HorizontalScroll } from "../../components/sections";
import { FiBox, FiMove, FiGitBranch, FiCloud, FiArrowUp, FiShield, FiZap, FiServer } from 'react-icons/fi';
import { usePageMetadata } from "../../hooks/usePageMetadata";

const CloudDevelopmentPage = () => {
  usePageMetadata({
    title: 'Cloud Development - Bercastle',
    description: 'Cloud-native applications with microservices, containers, and serverless architecture.',
    pageType: 'service',
    content: {
      service: {
        name: "Cloud Development",
        tagline: "Build for the Cloud",
        description: "Cloud-native applications designed to leverage microservices, containers, and serverless architecture for maximum scalability.",
        url: "/services/cloud-development"
      },
      capabilities: [
        {
          name: "Cloud-Native Application",
          description: "Building new applications specifically designed to run in the cloud.",
          features: ["Microservices architecture", "Container orchestration (Kubernetes)", "Serverless functions", "Auto-scaling", "High availability"]
        },
        {
          name: "Cloud Migration",
          description: "Migrating existing applications to the cloud with minimal downtime.",
          services: ["Assessment & planning", "Lift-and-shift migrations", "Re-architecting for cloud", "Data migration", "Post-migration optimization"]
        },
        {
          name: "DevOps & CI/CD",
          description: "Implement modern DevOps practices with automated pipelines.",
          solutions: ["CI/CD pipelines", "Infrastructure as Code", "Automated testing", "Monitoring & logging", "Container management"]
        },
        {
          name: "Multi-Cloud Solutions",
          description: "Deploy across multiple cloud providers for redundancy and performance."
        }
      ],
      platforms: {
        "AWS": ["EC2", "Lambda", "ECS", "RDS", "S3", "CloudFront"],
        "Azure": ["VMs", "Functions", "AKS", "SQL", "Blob Storage"],
        "Google Cloud": ["Compute Engine", "Cloud Functions", "GKE"]
      },
      benefits: ["Scalability", "Reliability", "Performance", "Security", "Cost-Effective", "Innovation"],
      process: ["Requirements & architecture design", "Environment setup", "Development & testing", "Security hardening", "Deployment automation", "Monitoring & optimization"],
      value_propositions: [
        "Multi-cloud expertise",
        "Security-first approach",
        "Cost optimization focus",
        "DevOps best practices",
        "24/7 support available",
        "Scalable solutions"
      ],
      meta: {
        source_url: "https://bercastle.com/services/cloud-development",
        service_category: "Cloud Development"
      }
    }
  });
  const cloudServices = [
    {
      icon: <FiBox size={24} />,
      title: 'Cloud-Native Application',
      description: 'Building new applications specifically designed to run in the cloud, leveraging microservices, containers, and serverless architecture.'
    },
    {
      icon: <FiMove size={24} />,
      title: 'Cloud Migration',
      description: 'Helping businesses move their existing applications and infrastructure to cloud platforms, optimizing for scalability and cost-efficiency.'
    },
    {
      icon: <FiGitBranch size={24} />,
      title: 'DevOps & CI/CD',
      description: 'Automated deployment processes and continuous integration/continuous delivery practices for rapid, reliable releases.'
    },
    {
      icon: <FiCloud size={24} />,
      title: 'Multi-Cloud Strategy',
      description: 'Design and implement solutions that leverage multiple cloud providers for optimal performance and cost efficiency.'
    }
  ];

  const advantages = [
    {
      icon: <FiArrowUp size={24} />,
      title: 'Scalability & Flexibility',
      description: 'Scale your applications effortlessly, adjust resources dynamically, and ensure optimal performance during peak times without over-provisioning.'
    },
    {
      icon: <FiShield size={24} />,
      title: 'Enhanced Resilience',
      description: 'Cloud-native applications are designed for fault tolerance, meaning they can recover quickly from failures and minimize downtime.'
    },
    {
      icon: <FiZap size={24} />,
      title: 'Faster Time to Market',
      description: 'With automated deployment and CI/CD practices, teams can release updates swiftly and respond quickly to market changes or customer feedback.'
    }
  ];

  const technologies = [
    {
      icon: <FiCloud size={24} />,
      title: 'Cloud Platforms',
      description: 'Amazon Web Services (AWS), Microsoft Azure, Google Cloud Platform (GCP), DigitalOcean, Linode'
    },
    {
      icon: <FiBox size={24} />,
      title: 'Containers & Orchestration',
      description: 'Docker, Kubernetes, Amazon ECS/EKS, Azure AKS, Google GKE'
    },
    {
      icon: <FiGitBranch size={24} />,
      title: 'DevOps Tools',
      description: 'Terraform, CloudFormation, Jenkins, GitHub Actions, Ansible, Chef, Puppet, Prometheus, Grafana'
    },
    {
      icon: <FiZap size={24} />,
      title: 'Serverless',
      description: 'AWS Lambda, Azure Functions, Google Cloud Functions, Serverless Framework'
    },
    {
      icon: <FiServer size={24} />,
      title: 'Databases',
      description: 'Amazon RDS, DynamoDB, Azure Cosmos DB, SQL Database, Cloud SQL, Firestore, MongoDB Atlas, Redis Cloud'
    },
    {
      icon: <FiMove size={24} />,
      title: 'Storage & CDN',
      description: 'Amazon S3, CloudFront, Azure Blob Storage, CDN, Google Cloud Storage, Cloudflare'
    }
  ];

  return (
    <Layout classmain="cloud-development-page">
      <Seo
        title='Cloud Development - OnScript AI'
        metaDesciption='Build scalable, secure cloud-native applications. Leverage cloud technologies for improved performance, reduced costs, and accelerated innovation.'
      />
      <Header />

      <ServiceHero 
        badge="Cloud Development"
        title="Build the Future in"
        titleHighlight="The Cloud"
        description="Build cloud-native applications that leverage the scalability, flexibility, and resilience of modern cloud platforms."
        ctaLink="/contact"
      />

      {/* Services Section */}
      <div className='py-20'>
        <div className='container-custom'>
          <CardColumn
            title="Cloud Development Services"
            description="Comprehensive cloud services from modernization to deployment"
            cards={cloudServices}
            cols={2}
            className='w-full'
          />
        </div>
      </div>

      {/* Advantages Section */}
      <div className='py-20'>
        <div className='container-custom'>
          <HorizontalScroll
            title="Cloud-Native Advantages"
            description="Modern development practices enable greater scalability, resilience, and agility"
            items={advantages}
            cardWidth={320}
          />
        </div>
      </div>

      {/* Technologies Section */}
      <div className='py-20'>
        <div className='container-custom'>
          <CardColumn
            title="Cloud Technologies We Use"
            description="Cutting-edge cloud platforms and tools"
            cards={technologies}
            cols={3}
            className='w-full'
          />
        </div>
      </div>

      <ServiceCTA 
        title="Ready to Move to the Cloud?"
        description="Let's build scalable, resilient cloud solutions that optimize your performance and reduce operational costs."
        buttonText="Start Your Cloud Journey"
      />

      <Footer />
    </Layout>
  )
}

export default CloudDevelopmentPage
